import { IconNames } from '@blueprintjs/icons';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { capitalizeString } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import { FieldArray, getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { cloneElement, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CardOptions } from 'components/valueCase/CardOptions';

const cardDepths = {
    TOP: 'top',
    MIDDLE: 'middle',
    BOTTOM: 'bottom',
};

/**
 * Deleting an element can be dependent on its dot notation path. Though the DynamicCards
 * component does not maintain this value, it may be stored in a state for referencing a
 * particular card. If the validity of the path of an element is dependent on the path of
 * the deleted element, then the path of the remaining element would need to be updated.
 * @param {String} deletedPath a string representing the dot notation path of the deleted element
 * @param {String} elementPath a string representing the dot notation path of the specified element
 * @returns a string representing the adjusted dot notation path of the specified element if
 * the elementPath is found to be dependent on the deletedPath.
 */
const updatePathsParentIndex = (deletedPath, elementPath) => {
    // deletedPath: tasks.0
    // elementPath: tasks.1.subtasks.0
    const deletedPathParts = deletedPath.split('.');
    const elementPathParts = elementPath.split('.');
    const deletedPathLength = deletedPathParts.length;
    const relativeElementPathParts = elementPathParts.slice(0, deletedPathLength);
    const deletedPathIndex = parseInt(deletedPathParts.at(-1), 10);
    const relativeElementPathIndex = parseInt(relativeElementPathParts.at(-1), 10);
    if (relativeElementPathIndex > deletedPathIndex) {
        elementPathParts[deletedPathLength - 1] = String(relativeElementPathIndex - 1);
        return elementPathParts.join('.');
    }
    return elementPath;
};
/**
 * Returns a nestable array of cards that allow for reordering, removing, adding, and editing nested
 * data in a formik FieldArray.
 * @param {string} id - dot notation path of the FieldArray
 * @param {string} arrayTitle - title of the array elements to be shown
 * @param {string} elementTitleAttribute - the key in the element of the array which provides a
 * title for the element
 * @param {string} elementName - a generic label for an individual element of the array
 * @param {string[]} relatedElementNames - array of element names that indicate a unique ID. These
 * will be replaced during the duplicate button call.
 * @param {node[]} elementComponents - array of renderable components
 * @param {function} newElementCallback - function that returns a new initialized data element for
 * the field array, called when the 'Add {elementName}' button is clicked
 * @param {('top'|'middle'|'bottom')} cardDepth - defines the presentation of the card based on
 * hierarchy
 * @param {function} editCallback - function called when edit button on element is clicked
 * @param {string} cardSelected - id of a selected card
 * @param {function} onCardClick - function called when a card is selected
 * @param {function} onCardDelete - function called when the remove button is clicked
 * @param {boolean} showArrayTitle - whether to show the array title
 * @param {boolean} showCardOptions - whether to show the card options (edit, remove, move)
 * @param {boolean} cardPreviewOnly - if only preview, cardOptions are not shown, and child form
 * fields are disabled
 * @param {string} key - key attribute of div that contains array
 * @param {string} className - className of div that contains array
 * @param {boolean} showErrorIndicator - whether formik errors should be shown for this card's id
 */
export const DynamicCards = ({
    id = undefined,
    arrayTitle = undefined,
    elementTitleAttribute = undefined,
    elementName,
    relatedElementNames = undefined,
    elementComponents = undefined,
    newElementCallback,
    cardDepth = cardDepths.TOP,
    editCallback = undefined,
    cardSelected = undefined,
    onCardClick = undefined,
    onCardDelete = undefined,
    showArrayTitle = true,
    showCardOptions = true,
    cardPreviewOnly = undefined,
    key = undefined,
    className = undefined,
    showErrorIndicator = true,
}) => {
    const formik = useFormikContext();

    const uniqueId = `${elementName}_id`;

    /**
     * An element can exist with either a distinctId or a uniqueId generated from the backend.
     * Finds the distinct/unique id (should only be one) if it exists.
     * @param {String} elementPath the dot notation path of the current element
     * @returns the id of the element.
     */
    const getId = useCallback(
        (elementPath) =>
            getIn(formik.values, `${elementPath}.distinctId`) ??
            getIn(formik.values, `${elementPath}.${uniqueId}`),
        [formik.values, uniqueId],
    );

    /**
     * Given the string value to use for the element's title
     * @param {String} elementPath the dot notation path of the current element
     * @param {String} defaultValue the value to use if the elementTitleAttribute is not valid
     * @returns the title to be used for an element
     */
    const getElementTitle = (elementPath, defaultValue) => {
        let elementTitle = defaultValue;
        if (elementTitleAttribute) {
            elementTitle = getIn(getIn(formik.values, elementPath), elementTitleAttribute);
            if (!(typeof elementTitle === 'string' && elementTitle.length > 0)) {
                elementTitle = defaultValue;
            }
        }
        return elementTitle;
    };

    /**
     * Generates new distinctIds for a nested object assuming the distinctId is in the format of
     * [namespace]_[uuid]
     * @param {*} element a object with a distinctId
     * @returns a copy of the element with the distinctIds replaced.
     */
    const duplicateElement = useCallback(
        (cardElement) => {
            const elementIds = new Set([
                ...(relatedElementNames ?? []).map((name) => `${name}_id`),
                uniqueId,
            ]);
            const duplicatedElement = structuredClone(cardElement);
            const elementKeys = Object.keys(cardElement);
            for (let keyIndex = 0; keyIndex < Object.keys(cardElement).length; keyIndex++) {
                if (elementKeys[keyIndex] === 'distinctId') {
                    duplicatedElement[elementKeys[keyIndex]] = uuidv4();
                } else if (elementIds.has(elementKeys[keyIndex])) {
                    delete duplicatedElement[elementKeys[keyIndex]];
                    duplicatedElement.distinctId = uuidv4();
                } else if (
                    typeof cardElement[elementKeys[keyIndex]] === 'object' &&
                    cardElement[elementKeys[keyIndex]] != null
                ) {
                    // eslint-disable-next-line react-hooks/immutability
                    const childElement = duplicateElement(cardElement[elementKeys[keyIndex]]);
                    if (Array.isArray(cardElement[elementKeys[keyIndex]])) {
                        duplicatedElement[elementKeys[keyIndex]] = Object.values(childElement);
                    } else {
                        duplicatedElement[elementKeys[keyIndex]] = childElement;
                    }
                }
            }
            return duplicatedElement;
        },
        [relatedElementNames, uniqueId],
    );

    return (
        <FieldArray name={id} validateOnChange>
            {(arrayHelpers) => (
                <Droppable droppableId={id} type={elementName}>
                    {(drop) => (
                        <div
                            key={key}
                            className={classNames('sds_dynamic-card__container', className)}
                            {...drop.droppableProps}
                            ref={drop.innerRef}
                        >
                            {(showArrayTitle || showCardOptions) && (
                                <div
                                    className={classNames(
                                        'sds_dynamic-card__flex-row',
                                        'sds_dynamic-card__flex-row--align-center',
                                        {
                                            'padding__bottom--medium': cardDepth === cardDepths.TOP,
                                            'padding__bottom--small': cardDepth !== cardDepths.TOP,
                                        },
                                    )}
                                >
                                    <div
                                        className={classNames(
                                            'font-semi-bold--uppercase',
                                            'sds_dynamic-card__flex-item--truncate',
                                        )}
                                    >
                                        {showArrayTitle ? arrayTitle : ''}
                                    </div>
                                    {showCardOptions && !cardPreviewOnly && (
                                        <ActionButtonText
                                            leftIcon={IconNames.ADD}
                                            label={`Add ${elementName}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newElement = newElementCallback();
                                                arrayHelpers.push(newElement);
                                            }}
                                            className="sds_dynamic-card__flex-item--maintain"
                                        />
                                    )}
                                </div>
                            )}
                            {getIn(formik.values, arrayHelpers.name)?.map((_element, index) => {
                                const elementPath = `${arrayHelpers.name}.${index}`;
                                const cardId = getId(elementPath);
                                const isSelectedCard = cardSelected === cardId;
                                const clickable = !!onCardClick && !isSelectedCard;
                                const onClick = () =>
                                    onCardClick({
                                        cardId,
                                        elementPath,
                                    });
                                const cardPreview = (
                                    <div
                                        key={cardId}
                                        className={classNames(
                                            'padding__around--medium',
                                            'sds_dynamic-card',
                                            {
                                                [`sds_dynamic-card__${cardDepth}`]: cardDepth,
                                            },
                                        )}
                                    >
                                        <div>
                                            <span>
                                                {getElementTitle(
                                                    elementPath,
                                                    `${capitalizeString(elementName)} ${index + 1}`,
                                                )}
                                            </span>
                                        </div>
                                        {elementComponents?.map((component) =>
                                            cloneElement(component, {
                                                id: component.props.id
                                                    ? `${elementPath}.${component.props.id}`
                                                    : elementPath,
                                                key: component.props.id
                                                    ? `${elementPath}.${component.props.id}`
                                                    : elementPath,
                                                cardPreviewOnly: true,
                                                disabled: true,
                                            }),
                                        )}
                                    </div>
                                );
                                const anyErrors = !!getIn(formik.errors, elementPath);
                                if (cardPreviewOnly) {
                                    return cardPreview;
                                }
                                return (
                                    <Draggable key={cardId} draggableId={cardId} index={index}>
                                        {(drag) => (
                                            <div
                                                className={classNames(
                                                    'padding__around--medium',
                                                    'sds_dynamic-card',
                                                    {
                                                        [`sds_dynamic-card__${cardDepth}`]:
                                                            cardDepth,
                                                        'sds_dynamic-card--highlight':
                                                            isSelectedCard,
                                                        'sds_dynamic-card--clickable': clickable,
                                                    },
                                                )}
                                                onClick={clickable ? () => onClick() : undefined}
                                                onKeyDown={clickable ? () => onClick() : undefined}
                                                role="menuitem"
                                                tabIndex={
                                                    onCardClick
                                                        ? (0 - isSelectedCard).toString()
                                                        : undefined
                                                }
                                                ref={drag.innerRef}
                                                {...drag.draggableProps}
                                                {...drag.dragHandleProps}
                                            >
                                                <div
                                                    className="padding__bottom--x-small"
                                                    style={{ display: 'flex' }}
                                                >
                                                    <span style={{ flexGrow: 1 }}>
                                                        <span>
                                                            {getElementTitle(
                                                                elementPath,
                                                                `${capitalizeString(elementName)} ${
                                                                    index + 1
                                                                }`,
                                                            )}
                                                        </span>
                                                        {anyErrors && showErrorIndicator && (
                                                            <Icon
                                                                icon={IconNames.ERROR}
                                                                intent={Icon.Intent.ERROR}
                                                                size={14}
                                                                tooltipContent="Missing required information"
                                                                className="sds_dynamic-card__card-errors"
                                                            />
                                                        )}
                                                    </span>
                                                    {showCardOptions && (
                                                        <CardOptions
                                                            arrayHelpers={arrayHelpers}
                                                            index={index}
                                                            elementPath={elementPath}
                                                            cardPreview={cardPreview}
                                                            onCardDelete={onCardDelete}
                                                            editCallback={editCallback}
                                                            duplicateElement={duplicateElement}
                                                            getId={getId}
                                                        />
                                                    )}
                                                </div>
                                                {elementComponents?.map((component) =>
                                                    cloneElement(component, {
                                                        id: component.props.id
                                                            ? `${elementPath}.${component.props.id}`
                                                            : elementPath,
                                                        key: `${
                                                            component.props.id
                                                                ? `${elementPath}.${component.props.id}`
                                                                : elementPath
                                                        }-${cardId}`,
                                                    }),
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {drop.placeholder}
                        </div>
                    )}
                </Droppable>
            )}
        </FieldArray>
    );
};

DynamicCards.propTypes = {
    id: PropTypes.string,
    arrayTitle: PropTypes.string,
    elementTitleAttribute: PropTypes.string,
    elementName: PropTypes.string.isRequired,
    relatedElementNames: PropTypes.arrayOf(PropTypes.string),
    elementComponents: PropTypes.arrayOf(PropTypes.node),
    newElementCallback: PropTypes.func.isRequired,
    cardDepth: PropTypes.oneOf(Object.values(cardDepths)),
    editCallback: PropTypes.func,
    cardSelected: PropTypes.string,
    onCardClick: PropTypes.func,
    onCardDelete: PropTypes.func,
    showCardOptions: PropTypes.bool,
    showArrayTitle: PropTypes.bool,
    cardPreviewOnly: PropTypes.bool,
    key: PropTypes.string,
    className: PropTypes.string,
    showErrorIndicator: PropTypes.bool,
};

DynamicCards.cardDepths = cardDepths;
DynamicCards.updatePathsParentIndex = updatePathsParentIndex;
